// Unified Workforce Calendar — types + presentation helpers for the gateway's
// aggregated feed (GET /api/v1/calendar/events). The gateway fans out to the
// owning services with the caller's own session, so everything here is already
// tenant-scoped and permission-filtered server-side.
// Contract doc: docs/calendar/UNIFIED_WORKFORCE_CALENDAR.md

// mine = my own calendar; direct = my IMMEDIATE reports; team = the whole
// reporting subtree.
//
// ⚠️ `direct` is the Team Management default. `team` walks the recursive
// subtree, which near the top of an org chart IS the company — Fern's Group
// CEO has 12 direct reports but 84 people under him out of 85 in the tenant,
// so his team calendar was the company calendar.
export type CalendarScope = 'mine' | 'direct' | 'team';

export type CalendarEventType =
  | 'leave'
  | 'holiday'
  | 'meeting'
  | 'one_on_one'
  | 'training_session'
  | 'goal_deadline'
  | 'self_assessment'
  | 'manager_review'
  | 'performance_cycle'
  | 'assignment'
  | 'work_item'
  | 'attendance_exception'
  | 'payroll'
  | 'statutory_deadline'
  | 'loan'
  | 'offboarding'
  | 'pay_date'
  | 'payroll_cutoff'
  | 'external';

export type CalendarEventStatus =
  | 'draft' | 'pending' | 'scheduled' | 'approved' | 'completed'
  | 'cancelled' | 'rejected' | 'at_risk' | 'overdue';

export interface CalendarEventAction {
  id: string;
  label: string;
  permission?: string;
  url?: string; // e.g. a "Join Meeting" link on an external event
}

/** Recognized conferencing providers on a synced external event. */
export type MeetingProvider =
  | 'GOOGLE_MEET' | 'MICROSOFT_TEAMS' | 'ZOOM' | 'WEBEX' | 'OTHER';

export interface WorkforceCalendarEvent {
  id: string; // stable: "<sourceService>:<sourceType>:<sourceId>"
  type: CalendarEventType;
  title: string;
  description?: string;
  startAt: string; // ISO datetime, or YYYY-MM-DD when allDay
  endAt?: string;
  allDay: boolean;
  status: CalendarEventStatus;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  progress?: number;
  personName?: string;
  personUserId?: string;
  location?: string;
  sourceService: string;
  sourceType: string;
  sourceId: string;
  deepLink?: string;
  // Set on synced external meetings: the recognized conferencing provider and a
  // join URL, so the UI can show a provider badge + "Join Meeting".
  meetingProvider?: MeetingProvider;
  meetingUrl?: string;
  metadata?: Record<string, unknown>;
  availableActions?: CalendarEventAction[];
}

export interface CalendarSourceStatus {
  source: string;
  ok?: boolean;
  skipped?: boolean;
  reason?: string;
}

export interface CalendarFeed {
  version: number;
  range: { from: string; to: string };
  scope: CalendarScope;
  events: WorkforceCalendarEvent[];
  sources: CalendarSourceStatus[];
  truncated: boolean;
  warnings: string[];
}

export const CALENDAR_EVENT_TYPES: CalendarEventType[] = [
  'leave', 'holiday', 'meeting', 'external', 'one_on_one', 'training_session', 'work_item', 'goal_deadline',
  'self_assessment', 'manager_review', 'performance_cycle',
  'assignment', 'attendance_exception', 'payroll', 'statutory_deadline', 'loan',
  'offboarding', 'pay_date', 'payroll_cutoff',
];

export interface CalendarTypeMeta {
  /** Default (untranslated) label; pages may override via i18n. */
  label: string;
  /** Chip classes — bg + text, light and dark. */
  chip: string;
  /** Solid dot class for the legend. */
  dot: string;
  /** Deadline-shaped types get countdown labels. */
  deadline?: boolean;
}

export const CALENDAR_TYPE_META: Record<CalendarEventType, CalendarTypeMeta> = {
  leave: { label: 'Time off', chip: 'bg-orange-100 text-orange-700 dark:bg-orange-500/[0.16] dark:text-orange-300', dot: 'bg-orange-400' },
  holiday: { label: 'Holiday', chip: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/[0.18] dark:text-indigo-300', dot: 'bg-indigo-400' },
  meeting: { label: 'Meeting', chip: 'bg-purple-100 text-purple-700 dark:bg-purple-500/[0.18] dark:text-purple-300', dot: 'bg-purple-400' },
  external: { label: 'External', chip: 'bg-slate-100 text-slate-700 dark:bg-slate-500/[0.18] dark:text-slate-300', dot: 'bg-slate-400' },
  one_on_one: { label: '1:1', chip: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/[0.18] dark:text-fuchsia-300', dot: 'bg-fuchsia-400' },
  training_session: { label: 'Training', chip: 'bg-pink-100 text-pink-700 dark:bg-pink-500/[0.18] dark:text-pink-300', dot: 'bg-pink-400' },
  goal_deadline: { label: 'Goal deadline', chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/[0.16] dark:text-emerald-300', dot: 'bg-emerald-400', deadline: true },
  self_assessment: { label: 'Self-assessment', chip: 'bg-teal-100 text-teal-700 dark:bg-teal-500/[0.16] dark:text-teal-300', dot: 'bg-teal-400', deadline: true },
  manager_review: { label: 'Manager review', chip: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/[0.16] dark:text-cyan-300', dot: 'bg-cyan-400', deadline: true },
  performance_cycle: { label: 'Review cycle', chip: 'bg-sky-100 text-sky-700 dark:bg-sky-500/[0.16] dark:text-sky-300', dot: 'bg-sky-400', deadline: true },
  assignment: { label: 'Assignment', chip: 'bg-blue-100 text-blue-700 dark:bg-blue-500/[0.16] dark:text-blue-300', dot: 'bg-blue-400' },
  work_item: { label: 'Work', chip: 'bg-violet-100 text-violet-700 dark:bg-violet-500/[0.18] dark:text-violet-300', dot: 'bg-violet-400' },
  attendance_exception: { label: 'Attendance', chip: 'bg-rose-100 text-rose-700 dark:bg-rose-500/[0.16] dark:text-rose-300', dot: 'bg-rose-400' },
  payroll: { label: 'Payroll', chip: 'bg-green-100 text-green-700 dark:bg-green-500/[0.16] dark:text-green-300', dot: 'bg-green-400' },
  statutory_deadline: { label: 'Statutory deadline', chip: 'bg-red-100 text-red-700 dark:bg-red-500/[0.16] dark:text-red-300', dot: 'bg-red-400', deadline: true },
  loan: { label: 'Loan repayment', chip: 'bg-amber-100 text-amber-700 dark:bg-amber-500/[0.16] dark:text-amber-300', dot: 'bg-amber-400', deadline: true },
  offboarding: { label: 'Offboarding', chip: 'bg-stone-200 text-stone-700 dark:bg-stone-500/[0.2] dark:text-stone-300', dot: 'bg-stone-400', deadline: true },
  pay_date: { label: 'Pay day', chip: 'bg-lime-100 text-lime-700 dark:bg-lime-500/[0.16] dark:text-lime-300', dot: 'bg-lime-400' },
  payroll_cutoff: { label: 'Payroll cutoff', chip: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/[0.16] dark:text-yellow-300', dot: 'bg-yellow-400', deadline: true },
};

/** Day-granularity countdown: "5 days remaining" / "Due tomorrow" / "Due today" / "3 days overdue". */
export function countdownLabel(dateIso: string, now: Date = new Date()): string | null {
  const target = String(dateIso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(target)) return null;
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const [y, m, d] = target.split('-').map(Number);
  const days = Math.round((Date.UTC(y, m - 1, d) - today) / 86_400_000);
  if (days > 1) return `${days} days remaining`;
  if (days === 1) return 'Due tomorrow';
  if (days === 0) return 'Due today';
  if (days === -1) return '1 day overdue';
  return `${-days} days overdue`;
}

/** Human label for a synced meeting's conferencing provider. */
export function meetingProviderLabel(p?: string): string | null {
  switch (p) {
    case 'GOOGLE_MEET': return 'Google Meet';
    case 'MICROSOFT_TEAMS': return 'Microsoft Teams';
    case 'ZOOM': return 'Zoom';
    case 'WEBEX': return 'Webex';
    case 'OTHER': return 'Online meeting';
    default: return null;
  }
}

/** Best-effort provider detection from a conference URL, mirroring
 *  leave-service `meeting_detect.py`. External synced meetings already arrive
 *  with `meetingProvider` set; this lets an INTERNAL meeting that only carries a
 *  link in `location` (e.g. a pasted Meet/Teams URL) show the same provider
 *  badge. Returns null for an unrecognized URL so a plain address isn't badged. */
export function detectMeetingProvider(url?: string | null): MeetingProvider | null {
  const u = (url || '').toLowerCase();
  if (!u) return null;
  if (/\bmeet\.google\.com\b/.test(u)) return 'GOOGLE_MEET';
  if (/\bteams\.microsoft\.com\b|\bteams\.live\.com\b/.test(u)) return 'MICROSOFT_TEAMS';
  if (/\bzoom\.us\b/.test(u)) return 'ZOOM';
  if (/\bwebex\.com\b/.test(u)) return 'WEBEX';
  return null;
}

/** The join URL for an event, if it exposes one (external meetings). */
export function joinUrl(ev: WorkforceCalendarEvent): string | null {
  if (ev.meetingUrl) return ev.meetingUrl;
  const a = (ev.availableActions || []).find((x) => x.id === 'join' && x.url);
  return a?.url || null;
}

/** yyyy-mm-dd span of an event (endAt falls back to startAt). */
export function eventDaySpan(ev: WorkforceCalendarEvent): { start: string; end: string } {
  const start = String(ev.startAt).slice(0, 10);
  const end = String(ev.endAt || ev.startAt).slice(0, 10);
  return { start, end };
}

export function eventsOnDay(events: WorkforceCalendarEvent[], dayIso: string): WorkforceCalendarEvent[] {
  return events.filter((ev) => {
    const { start, end } = eventDaySpan(ev);
    return start <= dayIso && end >= dayIso;
  });
}
